import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const PersonNavigationContext = createContext(null);

const MAX_TRAIL_LENGTH = 10;

export function PersonNavigationProvider({ children }) {
  const [visitedPersons, setVisitedPersons] = useState([]);
  const visitedRef = useRef(visitedPersons);
  visitedRef.current = visitedPersons;

  const pushPerson = useCallback((person) => {
    setVisitedPersons((prev) => {
      // Don't push if the person is already the last entry (prevents duplicates on breadcrumb nav)
      if (prev.length > 0 && prev[prev.length - 1].id === person.id) {
        return prev;
      }
      const updated = [...prev, person];
      if (updated.length > MAX_TRAIL_LENGTH) {
        return updated.slice(updated.length - MAX_TRAIL_LENGTH);
      }
      return updated;
    });
  }, []);

  const navigateToIndex = useCallback((index) => {
    // Truncate the trail to include only entries up to and including the given index
    // This removes all entries added after the selected breadcrumb entry (Req 3.7)
    setVisitedPersons((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      return prev.slice(0, index + 1);
    });
  }, []);

  const goBack = useCallback(() => {
    const current = visitedRef.current;
    if (current.length === 0) return null;
    const removed = current[current.length - 1];
    setVisitedPersons(current.slice(0, -1));
    return removed;
  }, []);

  const clearTrail = useCallback(() => {
    setVisitedPersons([]);
  }, []);

  const value = {
    visitedPersons,
    pushPerson,
    navigateToIndex,
    goBack,
    clearTrail,
  };

  return (
    <PersonNavigationContext.Provider value={value}>
      {children}
    </PersonNavigationContext.Provider>
  );
}

export function usePersonNavigation() {
  const context = useContext(PersonNavigationContext);
  if (!context) {
    throw new Error(
      'usePersonNavigation must be used within a PersonNavigationProvider'
    );
  }
  return context;
}

export default PersonNavigationContext;
